import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
    Button as MuiButton,
    CircularProgress,
} from '@mui/material';

/**
 * AppButton - A generic button component with consistent design
 * @param {Object} props
 * @param {string} [props.variant='contained'] - Button variant (contained, outlined, text)
 * @param {string} [props.color='primary'] - Button color (primary, secondary, error, warning, info, success)
 * @param {string} [props.size='medium'] - Button size (small, medium, large)
 * @param {boolean} [props.fullWidth=false] - Whether the button should take full width
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.loading=false] - Whether to show loading state
 * @param {React.ReactNode} [props.startIcon] - Icon to show before the button text
 * @param {React.ReactNode} [props.endIcon] - Icon to show after the button text
 * @param {Function} [props.onClick] - Click handler
 * @param {Object} [props.sx] - Additional styles
 */
const AppButton = forwardRef(({
    variant = 'contained',
    color = 'primary',
    size = 'medium',
    fullWidth = false,
    disabled = false,
    loading = false,
    startIcon,
    endIcon,
    onClick,
    children,
    sx,
    ...props
}, ref) => {
    const buttonStyles = {
        textTransform: 'none', // Prevents all-caps text
        borderRadius: 1, // Consistent border radius
        minWidth: size === 'small' ? 64 : 88, // Consistent minimum width
        fontWeight: 500, // Medium font weight
        ...sx
    };

    return (
        <MuiButton
            ref={ref}
            variant={variant}
            color={color}
            size={size}
            fullWidth={fullWidth}
            disabled={disabled || loading}
            startIcon={!loading && startIcon}
            endIcon={!loading && endIcon}
            onClick={onClick}
            sx={buttonStyles}
            {...props}
        >
            {loading ? (
                <>
                    <CircularProgress
                        size={size === 'small' ? 16 : 24}
                        sx={{
                            color: variant === 'contained' ? 'common.white' : `${color}.main`,
                            mr: 1
                        }}
                    />
                    {children}
                </>
            ) : children}
        </MuiButton>
    );
});

AppButton.propTypes = {
    variant: PropTypes.oneOf(['contained', 'outlined', 'text']),
    color: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    fullWidth: PropTypes.bool,
    disabled: PropTypes.bool,
    loading: PropTypes.bool,
    startIcon: PropTypes.node,
    endIcon: PropTypes.node,
    onClick: PropTypes.func,
    children: PropTypes.node,
    sx: PropTypes.object,
};

AppButton.displayName = 'AppButton';

export { AppButton };